<template>
  <div class="catalog-import-modal">
    <p class="text-muted text-sm mb-3">
      Importeer een Excel-bestand met leverancierscodes en keuzes.
      Het bestand wordt automatisch geanalyseerd.
    </p>

    <div class="row">
      <div class="col-lg-6">
        <TextInput
          id="catalog-name"
          v-model="form.name"
          label="Catalogus naam"
          placeholder="bijv. Screens productopties"
          :error="errors.name"
        />
      </div>
      <div class="col-lg-6">
        <TextInput
          id="catalog-category"
          v-model="form.productCategory"
          label="Productcategorie (optioneel)"
          placeholder="bijv. Screen, Rolluik"
        />
      </div>
    </div>

    <div class="mt-3">
      <FileInput
        id="catalog-file"
        v-model="form.file"
        label="Excel-bestand"
        accept=".xlsx,.xls"
        :error="errors.file"
      />
    </div>

    <div v-if="importResult" class="mt-3">
      <Message variant="success">
        Catalogus geïmporteerd: {{ importResult.stats?.fields_count || 0 }} velden,
        {{ importResult.stats?.values_count || 0 }} waarden.
      </Message>
    </div>

    <div v-if="importError" class="mt-3">
      <Message variant="danger" :text="importError" />
    </div>

    <div class="modal-actions mt-3">
      <Btn variant="primary" @click="handleImport" :loading="importing" :disabled="!isValid">
        Importeren
      </Btn>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue';
import { supplierCatalogService } from '@/services/supplierCatalogService';
import TextInput from '@/components/inputs/TextInput.vue';
import FileInput from '@/components/inputs/FileInput.vue';
import Message from '@/components/partials/Message.vue';
import Btn from '@/components/partials/Btn.vue';

export default defineComponent({
  name: 'CatalogImportModal',
  components: { TextInput, FileInput, Message, Btn },

  props: {
    supplierId: { type: String, required: true },
    onSubmit: { type: Function, default: null },
  },

  setup(props) {
    const importing = ref(false);
    const importResult = ref(null);
    const importError = ref(null);

    const form = ref({
      name: '',
      productCategory: '',
      file: null,
    });

    const errors = ref({});

    const isValid = computed(() => {
      return form.value.name?.trim() && form.value.file;
    });

    async function handleImport() {
      errors.value = {};
      importError.value = null;

      if (!form.value.name?.trim()) {
        errors.value.name = 'Naam is verplicht';
        return;
      }
      if (!form.value.file) {
        errors.value.file = 'Selecteer een bestand';
        return;
      }

      importing.value = true;
      try {
        const result = await supplierCatalogService.importCatalog(
          props.supplierId,
          form.value.file,
          form.value.name.trim(),
          form.value.productCategory?.trim() || null
        );
        importResult.value = result;

        if (props.onSubmit) {
          props.onSubmit(result);
        }
      } catch (e) {
        importError.value = e.response?.data?.message
          || 'Er ging iets mis bij het importeren.';
      } finally {
        importing.value = false;
      }
    }

    return {
      form,
      errors,
      isValid,
      importing,
      importResult,
      importError,
      handleImport,
    };
  },
});
</script>

<style scoped lang="scss">
.catalog-import-modal {
  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }
}

.mt-3 { margin-top: 1rem; }
.mb-3 { margin-bottom: 1rem; }
.text-muted { color: var(--text-secondary); }
.text-sm { font-size: 0.8125rem; }
</style>
